/**
 * CategoryCard Component
 *
 * Карточка категории услуг с многослойным дизайном согласно макету
 *
 * Особенности:
 * - Градиентный фон
 * - Многослойный эффект карточек
 * - Поддержка hover-состояний
 * - Семантические цвета из дизайн-системы
 * - Интернационализация через next-intl
 */

import { forwardRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { cn } from '@/src/shared/lib/utils';
import { Typography } from '@/src/shared/ui/typography/Typography';
import type { CategoryCardProps } from '@/src/entities/category';
import { getLocalizedName, type Locale, type Category } from '@hummii/shared-types';
import { mockCategories } from '@/src/entities/category/model/mock/mock-categories';
import { getCategoryImageUrl } from '@/src/entities/category/lib';

export const CategoryCard = forwardRef<HTMLDivElement, CategoryCardProps>(
  ({ category, subcategories: propSubcategories, locale: propLocale, onClick, onSubcategoryClick, className, ...props }, ref) => {
    const nextLocale = useLocale();
    const currentLocale = (propLocale || nextLocale) as Locale;
    const [imageError, setImageError] = useState(false);

    const handleClick = () => {
      if (onClick) {
        onClick(category.slug);
      }
    };

    const handleSubcategoryClick = (e: React.MouseEvent, subcategorySlug: string) => {
      e.stopPropagation(); // Prevent card click
      if (onSubcategoryClick) {
        onSubcategoryClick(subcategorySlug);
      }
    };

    // Get localized category name
    const translatedName = getLocalizedName(category, currentLocale);

    // Use provided subcategories or fallback to mock data
    const subcategories = propSubcategories && propSubcategories.length > 0
      ? propSubcategories.slice(0, 3)
      : mockCategories
          .filter((cat) => cat.parentId === category.id)
          .slice(0, 3);

    // Build subcategory data with name and slug
    const subcategoryData = subcategories.map((subcategory) => ({
      name: getLocalizedName(subcategory, currentLocale),
      slug: subcategory.slug,
    }));

    // Fallback to generic names if no real subcategories found (without slugs)
    const displaySubcategories = subcategoryData.length > 0
      ? subcategoryData
      : [
          { name: 'Service 1', slug: undefined },
          { name: 'Service 2', slug: undefined },
          { name: 'Service 3', slug: undefined },
        ];

    // Get the proper image URL (imageUrl from server has priority)
    const imageUrl = getCategoryImageUrl(
      category.icon,
      category.slug,
      'imageUrl' in category ? category.imageUrl : null
    );

    return (
      <div
        ref={ref}
        className={cn(
          // Base styles
          'relative h-[320px] w-full max-w-[320px] cursor-pointer overflow-hidden rounded-[32px] transition-all duration-300',
          className
        )}
        style={{ background: "var(--gradient-card)" }}
        onClick={handleClick}
        {...props}
      >
        {/* Subcategories List - Top Section */}
        <div className="relative z-20 flex flex-col p-4">
        {displaySubcategories.slice(0, 3).map((subcategory, index) => (
          <div
            key={`${category.id}-${index}`}
            onClick={subcategory.slug ? (e) => handleSubcategoryClick(e, subcategory.slug!) : undefined}
            className={cn(
              // Добавляем group для работы group-hover
              'group/subcategory',
              'w-full h-[100px] rounded-[16px] bg-background-primary px-5 py-4 transition-all duration-300',
              'shadow-[0_-4px_8px_rgba(0,0,0,0.1)] shadow-sm',
              // Hover эффекты на всю подкатегорию
              'hover:-translate-y-1 hover:shadow-md hover:shadow-[0_-6px_12px_rgba(0,0,0,0.15)]',
              // Добавляем cursor pointer только если есть slug
              subcategory.slug && 'cursor-pointer',
              index === 1 && 'delay-75 -mt-13',
              index === 2 && 'delay-150 -mt-13',
              index > 0 && '-mt-[38px]'
            )}
            style={{ zIndex: 20 + index }}
          >
            <Typography
              variant="bodySm"
              color="primary"
              weight="medium"
              className={cn(
                'line-clamp-1 transition-colors duration-200',
                // Текст становится зелёным при наведении на родительский div
                'group-hover/subcategory:text-accent-primary'
              )}
            >
              {subcategory.name}
            </Typography>
          </div>
        ))}
        </div>

        <div className="absolute bottom-0 right-0 w-full z-30" >
          <div className="absolute bottom-[100px] right-0">
            <svg width="199" height="18" viewBox="0 0 199 18" fill="none">
              <path d="M38.2873 1.61326C40.7743 0.548821 43.4515 0 46.1567 0H199V18H0L38.2873 1.61326Z" fill="#E1F7DB"/>
            </svg>           
          </div>
          <div className='absolute bottom-0 right-0 h-[100px] w-full bg-background-secondary'></div>
          <div className="absolute bottom-[-50px] right-[-60px] rounded-full h-[180px] w-[180px] overflow-hidden">
              {!imageError ? (
                <img
                  src={imageUrl}
                  alt={translatedName}
                  className="h-[120px] w-[250px] absolute top-[12px] right-0"
                  onError={() => {
                    // Prevent infinite loop by only setting error state once
                    if (!imageError) {
                      setImageError(true);
                    }
                  }}
                />
              ) : (
                // Fallback: Show empty div with subtle icon or nothing
                <div className="h-[120px] w-[250px] absolute top-[12px] right-0 flex items-center justify-center text-text-tertiary">
                  {/* Optional: Add a default icon here */}
                </div>
              )}
            </div>  
            {/* Title */}
            <div className="absolute bottom-6 left-6 z-20 max-w-[130px]">
            <Typography
              variant="h3"
              color="primary"
              weight="bold"
              className="line-clamp-2 leading-tight"
            >
              {translatedName}
            </Typography>
            </div>   
        </div>
      </div>
    );
  }
);

CategoryCard.displayName = 'CategoryCard';